import React, { useEffect, useState } from "react";
import { api, fmtMoney, formatErr } from "../../api";
import { downloadFile } from "../../download";
import { SectionTitle, StatusPill, EmptyState, Avatar } from "../../components/Atoms";
import { CurrencyDollarSimple, Check, PencilSimple, FileXls, X, CheckCircle, XCircle, HandCoins } from "@phosphor-icons/react";
import { useAuth } from "../../AuthContext";
import { toast } from "sonner";

const STATUSES = ["All","Requested","Pending","Approved","Paid","Rejected"];

export default function PayoutsPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [list, setList] = useState([]);
  const [editing, setEditing] = useState(null); // payout being edited
  const [form, setForm] = useState({ amount: 0, note: "", status: "Pending" });
  const [busy, setBusy] = useState(false);
  const [filter, setFilter] = useState("All");
  const [showCount, setShowCount] = useState(10);

  const load = async () => { const { data } = await api.get("/payouts"); setList(data); };
  useEffect(() => { load(); }, []);

  const openEdit = (p) => {
    setEditing(p);
    setForm({ amount: p.amount || 0, note: p.note || "", status: p.status || "Pending" });
  };

  const savePayout = async (e) => {
    e?.preventDefault();
    try {
      await api.put(`/payouts/${editing.id}`, {
        amount: Number(form.amount), status: form.status, note: form.note,
      });
      toast.success("Payout updated"); setEditing(null); load();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const act = async (id, action) => {
    try {
      await api.post(`/payouts/${id}/${action}`);
      toast.success(`Payout ${action}d`);
      load();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const exportXlsx = async () => {
    setBusy(true);
    try { await downloadFile("/reports/payouts/xlsx", "payouts-report.xlsx"); toast.success("Downloaded"); }
    catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(false); }
  };

  const totals = {
    paid: list.filter(p => p.status === "Paid").reduce((a, b) => a + (b.amount || 0), 0),
    approved: list.filter(p => p.status === "Approved").reduce((a, b) => a + (b.amount || 0), 0),
    requested: list.filter(p => p.status === "Requested" || p.status === "Pending").reduce((a, b) => a + (b.amount || 0), 0),
    rejected: list.filter(p => p.status === "Rejected").reduce((a, b) => a + (b.amount || 0), 0),
  };
  const filtered = filter === "All" ? list : list.filter(p => p.status === filter);
  const visible = filtered.slice(0, showCount);

  return (
    <div className="space-y-5">
      <SectionTitle title="Payouts" subtitle="Review creator requests, approve, and mark as paid"
        action={
          <button onClick={exportXlsx} disabled={busy} data-testid="payouts-export-xlsx"
            className="btn-primary flex items-center gap-2 disabled:opacity-50">
            <FileXls size={16} weight="bold" /> Export .xlsx
          </button>
        } />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <div className="glass p-4"><div className="text-[10px] uppercase font-bold text-muted tracking-wider">Requested</div><div className="text-2xl font-black text-[#F59E0B] mt-1">{fmtMoney(totals.requested)}</div></div>
        <div className="glass p-4"><div className="text-[10px] uppercase font-bold text-muted tracking-wider">Approved</div><div className="text-2xl font-black text-[#22D3EE] mt-1">{fmtMoney(totals.approved)}</div></div>
        <div className="glass p-4"><div className="text-[10px] uppercase font-bold text-muted tracking-wider">Paid</div><div className="text-2xl font-black text-[#10B981] mt-1">{fmtMoney(totals.paid)}</div></div>
        <div className="glass p-4"><div className="text-[10px] uppercase font-bold text-muted tracking-wider">Rejected</div><div className="text-2xl font-black text-[#EC4899] mt-1">{fmtMoney(totals.rejected)}</div></div>
      </div>

      <div className="flex gap-2 overflow-x-auto no-scrollbar">
        {STATUSES.map(s => (
          <button key={s} onClick={() => { setFilter(s); setShowCount(10); }} data-testid={`payout-tab-${s.toLowerCase()}`}
            className={`px-3 py-1.5 rounded-full text-xs font-bold whitespace-nowrap border ${filter === s ? "bg-white/10 text-white border-white/15" : "text-muted bg-white/5 border-white/5"}`}>
            {s} <span className="text-[10px] opacity-70">{s === "All" ? list.length : list.filter(p => p.status === s).length}</span>
          </button>
        ))}
      </div>

      {filtered.length === 0 ? <EmptyState icon={CurrencyDollarSimple} /> : (
        <>
        <div className="grid sm:grid-cols-2 gap-4">
          {visible.map(p => (
            <div key={p.id} className="glass p-4 hover-lift" data-testid={`payout-${p.id}`}>
              <div className="flex items-center gap-3">
                <Avatar src={p.ambassador_photo} name={p.ambassador_name} />
                <div className="flex-1 min-w-0">
                  <div className="text-white font-bold truncate">{p.ambassador_name}</div>
                  <div className="text-xs text-muted truncate">{p.project_title}</div>
                </div>
                <StatusPill status={p.status} />
              </div>
              {p.note && (
                <div className="mt-3 text-xs text-[#22D3EE] bg-[#22D3EE]/8 border border-[#22D3EE]/20 rounded-xl px-3 py-2">
                  📝 {p.note}
                </div>
              )}
              <div className="flex items-end justify-between mt-3 gap-2 flex-wrap">
                <div className="text-3xl font-black text-white">{fmtMoney(p.amount)}</div>
                {isAdmin && (
                  <div className="flex flex-wrap gap-2">
                    {(p.status === "Requested" || p.status === "Pending") && (
                      <>
                        <button onClick={() => act(p.id, "approve")} className="btn-ghost text-[#22D3EE] text-xs flex items-center gap-1.5"
                          data-testid={`approve-payout-${p.id}`}><CheckCircle size={14} weight="bold" /> Approve</button>
                        <button onClick={() => act(p.id, "reject")} className="btn-ghost text-[#EC4899] text-xs flex items-center gap-1.5"
                          data-testid={`reject-payout-${p.id}`}><XCircle size={14} weight="bold" /> Reject</button>
                      </>
                    )}
                    {p.status === "Approved" && (
                      <button onClick={() => act(p.id, "pay")} className="btn-primary text-xs flex items-center gap-1.5"
                        data-testid={`pay-payout-${p.id}`}><HandCoins size={14} weight="bold" /> Mark Paid</button>
                    )}
                    <button onClick={() => openEdit(p)} className="btn-ghost text-xs flex items-center gap-1.5"
                      data-testid={`edit-payout-${p.id}`}><PencilSimple size={14} weight="bold" /></button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
        {filtered.length > showCount && (
          <button onClick={() => setShowCount(c => c + 10)} className="btn-ghost w-full text-sm" data-testid="payouts-show-more">
            Show more ({filtered.length - showCount} hidden)
          </button>
        )}
        </>
      )}

      {editing && (
        <div className="fixed inset-0 z-50 grid place-items-center p-4" onClick={() => setEditing(null)}>
          <div className="absolute inset-0 bg-black/75 backdrop-blur-sm" />
          <form onSubmit={savePayout} className="glass relative p-6 w-full max-w-md max-h-[90vh] overflow-y-auto space-y-4" onClick={e => e.stopPropagation()}>
            <div className="flex items-start justify-between">
              <div>
                <div className="text-white text-xl font-bold">Personalise Payout</div>
                <div className="text-xs text-muted mt-1">{editing.ambassador_name} · {editing.project_title}</div>
              </div>
              <button type="button" onClick={() => setEditing(null)} className="text-muted hover:text-white"><X size={20} /></button>
            </div>
            <div>
              <label className="text-xs uppercase font-bold text-muted tracking-wider">Amount (₹)</label>
              <input type="number" min="0" step="0.01" value={form.amount}
                onChange={e => setForm({ ...form, amount: e.target.value })}
                className="input-base mt-1 text-2xl font-black" data-testid="payout-amount-input" />
            </div>
            <div>
              <label className="text-xs uppercase font-bold text-muted tracking-wider">Status</label>
              <div className="grid grid-cols-3 gap-2 mt-2">
                {["Requested", "Approved", "Paid", "Rejected", "Pending"].map(s => (
                  <button type="button" key={s} onClick={() => setForm({ ...form, status: s })}
                    data-testid={`payout-status-${s.toLowerCase()}`}
                    className={`py-2 rounded-xl text-xs font-bold border ${form.status === s ? "bg-white/10 border-white/20 text-white" : "bg-white/5 border-white/10 text-muted"}`}>
                    {s}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="text-xs uppercase font-bold text-muted tracking-wider">Note (optional)</label>
              <textarea rows={3} value={form.note}
                onChange={e => setForm({ ...form, note: e.target.value })}
                placeholder="e.g. ₹500 bonus for top performance, paid via UPI ref #1234"
                className="input-base mt-1" data-testid="payout-note-input" />
              <div className="text-[10px] text-muted mt-1">Visible to the ambassador and stored in reports.</div>
            </div>
            <div className="flex gap-2 pt-1">
              <button type="button" onClick={() => setEditing(null)} className="btn-ghost flex-1">Cancel</button>
              <button type="submit" className="btn-primary flex-1" data-testid="save-payout-btn">Save</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
